import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, CircularProgress } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';

import authService from '../../shared/services/authService';
import codelistService from '../../services/codelistService';

/** Admin "Data Settings" — refresh ATLAS codelists into the DB/cache. */
const CodelistRefreshPanel: React.FC = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<string | null>(null);

  useEffect(() => {
    authService
      .getCodelistLastRefreshed()
      .then((data) => {
        if (data?.lastRefreshed) setLastRefreshed(data.lastRefreshed);
      })
      .catch(() => {});
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const result = await authService.refreshCodelists();
      if (result.status === 'success') {
        await codelistService.forceRefresh();
        window.dispatchEvent(new Event('codelist:refreshed'));
        toast.success(
          `Codelists refreshed — ${result.typesRefreshed} types, ${result.recordsUpserted} records updated. Form dropdowns updated.`,
        );
        setLastRefreshed(result.refreshedAt);
      } else {
        toast.info(result.message || 'No new data from Atlas API');
      }
    } catch (error) {
      toast.error('Refresh failed. Please try again or contact support.');
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3 }}>
        Data Management
      </Typography>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Refresh Codelists
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Fetches the latest codelist values from Atlas and updates all dropdowns immediately.
            </Typography>
            {lastRefreshed && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                Last refreshed: {new Date(lastRefreshed).toLocaleString()}
              </Typography>
            )}
          </Box>
          <Button
            variant="contained"
            startIcon={isRefreshing ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
            onClick={handleRefresh}
            disabled={isRefreshing}
            sx={{ minWidth: 180 }}
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh Codelists'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default CodelistRefreshPanel;
